import { FILL_OPTION, VISIT_TYPES, type PatientVisit } from "~/services";
import { RELATIONSHIP_TO_POLICY_HOLDER_OPTIONS } from "../components/VisitForm/constants";
import { PersonalInformationFormData } from "../validation/schema";

type StorePatientData = {
  visitDateTime?: string;
  patientEmail?: string;
  appointmentType?: string;
  providerId?: number;
  patientStateId?: number | null;
  insuranceId?: number | null;
};

type ProcessFileFunction = (
  file: File | string | null | undefined,
) => File | string | null;

type VisitTypeConfig = {
  [key: string]: {
    includeReferral: boolean;
    includeInsurance: boolean;
  };
};

const visitTypeConfig: VisitTypeConfig = {
  [VISIT_TYPES.INITIAL]: {
    includeReferral: true,
    includeInsurance: true,
  },
  [VISIT_TYPES.FOLLOW_UP]: {
    includeReferral: false,
    includeInsurance: true,
  },
  [VISIT_TYPES.COACH]: {
    includeReferral: false,
    includeInsurance: true,
  },
};

export const processFile: ProcessFileFunction = (file) => {
  if (file instanceof File) {
    return file;
  }
  return file || null;
};

export const getVisitTypeConfig = (visitType: string) => {
  return (
    visitTypeConfig[visitType] || {
      includeReferral: false,
      includeInsurance: false,
    }
  );
};

export const buildBaseFields = (
  data: PersonalInformationFormData,
  storePatientData: StorePatientData | null,
  userEmail: string | null,
) => {
  const hasPersonalInfo = "firstName" in data;

  return {
    patientFirstName: hasPersonalInfo ? data.firstName : undefined,
    patientLastName: hasPersonalInfo ? data.lastName : undefined,
    patientDob: hasPersonalInfo ? data.dateOfBirth : undefined,
    patientSexAtBirth: hasPersonalInfo ? data.sexAtBirth : undefined,
    patientMobilePhoneNumber: hasPersonalInfo ? data.mobilePhone : undefined,

    patientEmail: userEmail || storePatientData?.patientEmail,

    patientStateId: data.state
      ? parseInt(data.state)
      : storePatientData?.patientStateId || 0,
    patientAddressOne: data.homeAddress1,
    patientAddressTwo: data.homeAddress2,
    patientCity: data.city,
    patientZipCode: data.zipCode,
  };
};

export const buildReferralFields = (data: PersonalInformationFormData) => ({
  patientHowDidYouHear:
    "referralSource" in data ? data.referralSource : undefined,
});

export const buildInsuranceFields = (
  data: PersonalInformationFormData,
  storePatientData: StorePatientData | null,
) => {
  const fields: Partial<PatientVisit> = {
    insuranceId: storePatientData?.insuranceId,
  };

  // Self-pay schemas don't include memberId — skip insurance fields entirely.
  if (!("memberId" in data)) {
    return fields;
  }

  // RHF keeps field values after unmounting (shouldUnregister defaults to false),
  // so memberId may be present even when the patient selected "later". Guard explicitly.
  if (
    "fillInsuranceInfo" in data &&
    data.fillInsuranceInfo === FILL_OPTION.LATER
  ) {
    return fields;
  }

  type InsuranceNowFields = {
    relationshipToPolicyHolder: string;
    memberId: string;
    insuranceCardFront?: File | null;
    insuranceCardBack?: File | null;
    insuranceCompanyName?: string;
    policyHolderFirstName?: string | null;
    policyHolderLastName?: string | null;
    policyHolderDateOfBirth?: string | null;
  };

  const d = data as InsuranceNowFields;

  fields.insuranceRelationshipToPolicyHolder = d.relationshipToPolicyHolder;
  fields.insuranceCardFront = processFile(d.insuranceCardFront);
  fields.insuranceCardBack = processFile(d.insuranceCardBack);
  fields.insurancePolicyNumber = d.memberId;

  if (!storePatientData?.insuranceId) {
    fields.insuranceCompanyName = d.insuranceCompanyName;
  }

  if (
    d.relationshipToPolicyHolder !== RELATIONSHIP_TO_POLICY_HOLDER_OPTIONS.SELF
  ) {
    fields.insurancePolicyHolderFirstName =
      d.policyHolderFirstName || undefined;
    fields.insurancePolicyHolderLastName = d.policyHolderLastName || undefined;
    fields.insurancePolicyHolderDob = d.policyHolderDateOfBirth || undefined;
  }

  return fields;
};

export const buildConditionalFields = (
  data: PersonalInformationFormData,
  visitType: string,
  storePatientData: StorePatientData | null,
) => {
  const config = getVisitTypeConfig(visitType);
  const conditionalFields: Partial<PatientVisit> = {};

  if (config.includeReferral) {
    Object.assign(conditionalFields, buildReferralFields(data));
  }

  if (config.includeInsurance) {
    Object.assign(
      conditionalFields,
      buildInsuranceFields(data, storePatientData),
    );
  }

  return conditionalFields;
};

export const buildPatientVisitData = (
  data: PersonalInformationFormData,
  visitType: string,
  storePatientData: StorePatientData | null,
  userEmail: string | null,
  nirvanaInsuranceKey?: string | null,
): PatientVisit => {
  const baseFields = buildBaseFields(data, storePatientData, userEmail);
  const conditionalFields = buildConditionalFields(
    data,
    visitType,
    storePatientData,
  );

  return {
    visitDateTime: storePatientData?.visitDateTime || "",
    appointmentType: storePatientData?.appointmentType || visitType,
    providerId: storePatientData?.providerId || 0,
    stateId: storePatientData?.patientStateId || 0,

    ...baseFields,

    ...conditionalFields,

    ...(nirvanaInsuranceKey
      ? { nirvana_insurance_key: nirvanaInsuranceKey }
      : {}),
  } as PatientVisit;
};
